import { supabase } from './supabase';
import { unifiedStorageManager } from './unifiedStorageManager';

/**
 * Check if a resource is active for the given auth user ID
 */
export async function checkResourceActive(authUserId: string) {
  if (!supabase) {
    return {
      isActive: false,
      error: 'Supabase client not initialized'
    };
  }

  try {
    const { data, error } = await supabase
      .from('resources')
      .select('id, is_active')
      .eq('auth_user_id', authUserId)
      .order('is_active', { ascending: false })
      .limit(1);

    if (error) throw error;

    return {
      isActive: data?.[0]?.is_active === true,
      error: null
    };
  } catch (err) {
    return {
      isActive: false,
      error: err instanceof Error ? err.message : 'Unknown error checking resource status'
    };
  }
}

/**
 * Comprehensive sign out function that clears all cached data and storage
 */
export async function signOut() {

  if (!supabase) return { error: 'Supabase client not initialized' };
  
  try {
    console.log('🚪 Starting comprehensive sign out process...');

    // 0. Unregister this device from push notifications (must run while we still have a session)
    try {
      const { getPushDeviceToken, setPushDeviceToken } = await import('./pushDeviceToken');
      const token = getPushDeviceToken();
      if (token) {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.access_token) {
          await supabase.functions.invoke('unregister-push-device', {
            body: { device_token: token },
            headers: { Authorization: `Bearer ${session.access_token}` },
          });
        }
        setPushDeviceToken(null);
      } else {
        // Web/PWA: remove this device's push subscription so it stops receiving after logout
        const { pushNotificationService } = await import('./pushNotifications');
        if (pushNotificationService?.removeSubscription) {
          await pushNotificationService.removeSubscription().catch(() => {});
        }
      }
    } catch (e) {
      console.warn('Failed to unregister push device on logout:', e);
    }

    // 1. Sign out from Supabase (this triggers auth state change events)
    console.log('🔍 About to call supabase.auth.signOut()...');
    console.log('🔍 Supabase client status:', supabase ? 'Available' : 'Not available');
    console.log('🔍 Supabase auth status:', supabase?.auth ? 'Available' : 'Not available');
    
    try {
      // CRITICAL: Sign out with scope: 'local' to only sign out this device, not all devices
      // This prevents logout on one device from affecting other devices
      // Note: Supabase's signOut() by default uses 'local' scope, but we're being explicit
      const { error } = await supabase.auth.signOut({ scope: 'local' });
      if (error) {
        console.warn('Supabase sign out error:', error);
        // Try global signout as fallback if local fails
        const { error: globalError } = await supabase.auth.signOut();
        if (globalError) {
          console.warn('Global sign out also failed:', globalError);
        }
      } else {
        console.log('✅ Supabase sign out successful (local scope)');
      }
    } catch (signOutError) {
      console.error('❌ Exception during supabase.auth.signOut():', signOutError);
      // Continue with cleanup even if Supabase sign out fails
    }
    
    // 2. Clear authStateManager state IMMEDIATELY to allow fresh login detection
    // This must happen before waiting for auth state changes
    try {
      const { authStateManager } = await import('./authStateManager');
      authStateManager.clearState();
      console.log('✅ Cleared AuthStateManager state');
    } catch (e) {
      console.warn('Failed to clear AuthStateManager state:', e);
    }
    
    // 3. Wait for auth state change events to propagate (reduced delay for better UX)
    // Supabase cleanup is fast, we don't need 500ms
    await new Promise(resolve => setTimeout(resolve, 150));
    
    // 4. Clear all auth-related localStorage items
    // CRITICAL: Clear ALL business and routing data to ensure fresh login
    // ALSO: Clear MultiSessionManager data (unused feature that causes conflicts)
    const authKeysToRemove = [
      'cachedUserSession',
      'enhancedLoginInProgress',
      'skipInitializing',
      'preloadedRoute',
      'usePreloadedBusinessData',
      'selectedBusiness',
      'businesses',
      'preloadedCalendarData', // Clear calendar preload data
      'nexus-auth-backup',
      'nexus-auth-backup-timestamp',
      'nexus-booking-auth',
      'nexus_impersonation_data',
      'homeScreenRoute', // Clear home screen route cache
      'lastSelectedBusinessId', // Clear any business selection cache
      'businessContext', // Clear any business context cache
      'userBusinesses', // Clear any cached business list
      'currentBusiness', // Clear current business reference
      'activeBusinessSessions', // CRITICAL: Clear MultiSessionManager sessions
      'currentActiveSessionId', // CRITICAL: Clear MultiSessionManager current session
    ];
    
    // Remove specific auth keys
    authKeysToRemove.forEach(key => {
      try {
        localStorage.removeItem(key);
        console.log(`🧹 Removed localStorage key: ${key}`);
      } catch (e) {
        console.warn(`Failed to remove localStorage key ${key}:`, e);
      }
    });
    
    // 4. Clear Supabase auth storage (but be selective to avoid interfering with other data)
    // Supabase uses 'sb-<project-ref>-auth-token' format
    // We'll clear auth-related keys but let Supabase handle its own cleanup
    try {
      const supabaseAuthKeys = Object.keys(localStorage).filter(key => 
        key.startsWith('sb-') && key.includes('auth-token')
      );
      
      supabaseAuthKeys.forEach(key => {
        try {
          localStorage.removeItem(key);
          console.log(`🧹 Removed Supabase auth key: ${key}`);
        } catch (e) {
          console.warn(`Failed to remove Supabase auth key ${key}:`, e);
        }
      });
      
      // Also clear our custom nexus-booking-auth key (already in authKeysToRemove above)
      // But don't clear ALL keys with 'auth' in them as that's too broad
    } catch (e) {
      console.warn('Failed to clear Supabase auth keys:', e);
    }
    
    // 6. Clear sessionStorage
    try {
      sessionStorage.clear();
      console.log('🧹 Cleared sessionStorage');
    } catch (e) {
      console.warn('Failed to clear sessionStorage:', e);
    }
    
    // 7. Clear unified storage (IndexedDB)
    try {
      await unifiedStorageManager.removeItem('nexus-auth-backup');
      await unifiedStorageManager.removeItem('nexus-auth-backup-timestamp');
      await unifiedStorageManager.removeItem('nexus-booking-auth');
      console.log('🧹 Cleared unified storage');
    } catch (e) {
      console.warn('Failed to clear unified storage:', e);
    }
    
    // 7. Clear IndexedDB auth data (but don't delete entire databases)
    // Deleting entire databases is too aggressive and can interfere with subsequent logins
    // Instead, we'll let Supabase handle its own IndexedDB cleanup
    // Only clear our custom IndexedDB items
    if ('indexedDB' in window) {
      try {
        // Clear our custom auth storage items from IndexedDB
        // Supabase will handle its own IndexedDB cleanup via signOut()
        console.log('🧹 IndexedDB cleanup handled by Supabase signOut()');
      } catch (e) {
        console.warn('Failed to handle IndexedDB cleanup:', e);
      }
    }
    
    // 9. Clear only auth-related caches, not all service worker caches
    // Clearing all caches causes page reload issues and poor performance
    // Service worker caches should persist for better UX
    if ('caches' in window) {
      try {
        const cacheNames = await caches.keys();
        // Only clear caches that contain auth data, not all caches
        const authCaches = cacheNames.filter(name => 
          name.includes('auth') || 
          name.includes('supabase-auth') ||
          name.includes('api-cache')
        );
        
        await Promise.all(
          authCaches.map(cacheName => {
            console.log(`🧹 Clearing auth cache: ${cacheName}`);
            return caches.delete(cacheName);
          })
        );
        console.log('🧹 Cleared auth-related caches (service worker caches preserved)');
      } catch (e) {
        console.warn('Failed to clear auth caches:', e);
      }
    }
    
    // 10. Service workers: preserve for PWA; native iOS push was already unregistered in step 0
    if ('serviceWorker' in navigator) {
      try {
        console.log('✅ Service worker preserved (native push device was unregistered in step 0)');
      } catch (e) {
        console.warn('Failed to handle service workers on logout:', e);
      }
    }
    
    // 11. Clear any remaining auth state - be more aggressive
    // CRITICAL: Do NOT clear push notification related keys
    // Push subscriptions are stored in browser PushManager and database, not localStorage
    // But we protect against accidentally clearing any push-related keys just to be safe
    try {
      // Clear any remaining auth-related data
      // EXCLUDE push notification keys to ensure subscriptions are never affected
      const remainingAuthKeys = Object.keys(localStorage).filter(key => 
        (key.includes('auth') || 
         key.includes('session') || 
         key.includes('token') || 
         key.includes('user') ||
         key.includes('business') ||
         key.includes('route') ||
         key.includes('preload') ||
         key.includes('skip')) &&
        // CRITICAL: Never clear push notification related keys
        !key.includes('push') &&
        !key.includes('notification') &&
        !key.includes('subscription')
      );
      
      remainingAuthKeys.forEach(key => {
        try {
          localStorage.removeItem(key);
          console.log(`🧹 Removed remaining auth/business key: ${key}`);
        } catch (e) {
          console.warn(`Failed to remove remaining auth key ${key}:`, e);
        }
      });
      
      console.log('✅ Push notification subscriptions preserved (stored in browser PushManager, not localStorage)');
    } catch (e) {
      console.warn('Failed to clear remaining auth data:', e);
    }
    
    // 12. Verify logout is complete - ensure no session remains
    try {
      const { data: { session: verifySession } } = await supabase.auth.getSession();
      if (verifySession) {
        console.warn('⚠️ Session still exists after logout - forcing additional cleanup');
        // Force another sign out attempt
        await supabase.auth.signOut();
        await new Promise(resolve => setTimeout(resolve, 100));
      } else {
        console.log('✅ Logout verified - no session remains');
      }
    } catch (verifyError) {
      console.warn('Failed to verify logout completion:', verifyError);
    }
    
    // 13. Final wait to ensure all cleanup completes and state propagates
    // This gives BusinessContext and other listeners time to process SIGNED_OUT event
    await new Promise(resolve => setTimeout(resolve, 200));
    
    console.log('✅ Comprehensive sign out completed successfully - ready for fresh login');
    return { error: null };
    
  } catch (err) {
    console.error('❌ Error during comprehensive sign out:', err);
    return { error: err instanceof Error ? err.message : 'Unknown error during sign out' };
  }
} 

/**
 * Utility function to clear all cached data and storage without signing out
 * Useful for debugging or manual cleanup
 */
export async function clearAllStorage(): Promise<void> {
  try {
    console.log('🧹 Starting comprehensive storage cleanup...');
    
    // 1. Clear all auth-related localStorage items
    const authKeysToRemove = [
      'cachedUserSession',
      'enhancedLoginInProgress',
      'skipInitializing',
      'preloadedRoute',
      'usePreloadedBusinessData',
      'selectedBusiness',
      'businesses',
      'nexus-auth-backup',
      'nexus-auth-backup-timestamp',
      'nexus-booking-auth',
      'nexus_impersonation_data'
    ];
    
    // Remove specific auth keys
    authKeysToRemove.forEach(key => {
      try {
        localStorage.removeItem(key);
        console.log(`🧹 Removed localStorage key: ${key}`);
      } catch (e) {
        console.warn(`Failed to remove localStorage key ${key}:`, e);
      }
    });
    
    // 2. Clear all Supabase-related localStorage items
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('sb-') || key.startsWith('supabase.auth.') || key.includes('auth')) {
        try {
          localStorage.removeItem(key);
          console.log(`🧹 Removed Supabase localStorage key: ${key}`);
        } catch (e) {
          console.warn(`Failed to remove Supabase localStorage key ${key}:`, e);
        }
      }
    });
    
    // 3. Clear sessionStorage
    try {
      sessionStorage.clear();
      console.log('🧹 Cleared sessionStorage');
    } catch (e) {
      console.warn('Failed to clear sessionStorage:', e);
    }
    
    // 4. Clear unified storage (IndexedDB)
    try {
      await unifiedStorageManager.removeItem('nexus-auth-backup');
      await unifiedStorageManager.removeItem('nexus-auth-backup-timestamp');
      await unifiedStorageManager.removeItem('nexus-booking-auth');
      console.log('🧹 Cleared unified storage');
    } catch (e) {
      console.warn('Failed to clear unified storage:', e);
    }
    
    // 5. Clear IndexedDB databases
    if ('indexedDB' in window) {
      try {
        // Clear supabase-auth database
        const supabaseRequest = indexedDB.deleteDatabase('supabase-auth');
        supabaseRequest.onsuccess = () => console.log('🧹 Cleared supabase-auth IndexedDB');
        supabaseRequest.onerror = () => console.warn('Failed to clear supabase-auth IndexedDB');
        
        // Clear nexus-auth-storage database
        const nexusRequest = indexedDB.deleteDatabase('nexus-auth-storage');
        nexusRequest.onsuccess = () => console.log('🧹 Cleared nexus-auth-storage IndexedDB');
        nexusRequest.onerror = () => console.warn('Failed to clear nexus-auth-storage IndexedDB');
        
        // Clear nexus-unified-storage database
        const unifiedRequest = indexedDB.deleteDatabase('nexus-unified-storage');
        unifiedRequest.onsuccess = () => console.log('🧹 Cleared nexus-unified-storage IndexedDB');
        unifiedRequest.onerror = () => console.warn('Failed to clear nexus-unified-storage IndexedDB');
      } catch (e) {
        console.warn('Failed to clear IndexedDB databases:', e);
      }
    }
    
    // 6. Clear service worker cache (if available)
    if ('caches' in window) {
      try {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => {
            console.log(`🧹 Clearing cache: ${cacheName}`);
            return caches.delete(cacheName);
          })
        );
        console.log('🧹 Cleared all service worker caches');
      } catch (e) {
        console.warn('Failed to clear service worker caches:', e);
      }
    }
    
    // 7. Clear any remaining auth state
    try {
      // Clear any remaining auth-related data
      const remainingAuthKeys = Object.keys(localStorage).filter(key => 
        key.includes('auth') || key.includes('session') || key.includes('token') || key.includes('user')
      );
      
      remainingAuthKeys.forEach(key => {
        try {
          localStorage.removeItem(key);
          console.log(`🧹 Removed remaining auth key: ${key}`);
        } catch (e) {
          console.warn(`Failed to remove remaining auth key ${key}:`, e);
        }
      });
    } catch (e) {
      console.warn('Failed to clear remaining auth data:', e);
    }
    
    console.log('✅ Comprehensive storage cleanup completed successfully');
  } catch (err) {
    console.error('❌ Error during storage cleanup:', err);
    throw err;
  }
} 